import React, { FC, ReactNode } from "react"
import Image from "next/image"
import { ColorModeContent } from "components/core/colorModeContent"
import { PageTitle } from "components/core/pageTitle"
import { Container } from "components/core/container"
import { Link } from "components/core/link"
import { Divider } from "./core/divider"
import { Card } from "./core/card"

export const Changelog: FC = () => (
  <div className="bg-white dark:bg-gray-900">
    <Container>
      <PageTitle
        title="Changelog"
        subtitle={
          <div className="flex items-center justify-center">
            Follow us on
            <Link to="https://twitter.com/fetoolkitio" target="_blank" rel="noreferrer noopener" className="ml-2">
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
              @fetoolkitio
            </Link>
          </div>
        }
      />

      <div className="max-w-3xl mx-auto">
        <Entry
          date="May 10, 2023"
          intro="AI enhanced JS / TS Converter"
          additions={[<>User on the paid plan can now use AI to transform JavaScript code to TypeScript</>]}
          improvements={["Many small bug fixes and improvements to existing widgets"]}
        />

        <Entry
          date="July 17, 2022"
          intro="Password protected dashboards 🔐"
          additions={[
            <>
              User on the paid plan can now have password protected dashboards
              <div className="my-4">
                <ColorModeContent
                  dark={
                    <Image
                      src="/assets/screenshots/protectedDashboardDark.jpg"
                      alt="Password protected dashboards"
                      width="676"
                      height="322"
                      className="rounded-lg shadow-md mt-1 mb-2"
                    />
                  }
                  light={
                    <Image
                      src="/assets/screenshots/protectedDashboard.jpg"
                      alt="Password protected dashboards"
                      width="676"
                      height="322"
                      className="rounded-lg shadow-md mt-1 mb-2"
                    />
                  }
                />
              </div>
            </>,
          ]}
          improvements={["Many small bug fixes and improvements to existing widgets"]}
        />

        <Entry
          date="February 6, 2022"
          intro="Launched version 2 of Frontend Toolkit which brings many new features 🚀"
          additions={[
            "Each user can have multiple dashboards",
            "Dashboard(s) can be shared with other users",
            "Dashboard(s) can be made private",
            "Fork dashboard(s) from other people",
            "Add your own custom content to your dashboard",
          ]}
          improvements={["Many small bug fixes and improvements to almost all existing widgets"]}
        />

        <Entry
          date="July 8, 2021"
          additions={[
            <>
              Added <Link to="/widget/url-converter">URL encoder/decoder widget</Link>
            </>,
          ]}
          improvements={[
            <>
              The package size widget now contains a chart to check the size evolution over different versions (similar
              to what bundlephobia shows)
              <div className="my-4">
                <ColorModeContent
                  dark={
                    <Image
                      src="/assets/screenshots/packageSizeChartDark.jpg"
                      alt="Package size chart"
                      width="736"
                      height="523"
                      className="rounded-lg shadow-md mt-1 mb-2"
                    />
                  }
                  light={
                    <Image
                      src="/assets/screenshots/packageSizeChart.jpg"
                      alt="Package size chart"
                      width="736"
                      height="523"
                      className="rounded-lg shadow-md mt-1 mb-2"
                    />
                  }
                />
              </div>
            </>,
            <>Improved the package size search (it now shows bundle build errors and includes more packages)</>,
            <>
              The selected package is now stored in the url and can therefore be shared (e.g.{" "}
              <Link to="/widget/package-size?package=react-dom">react-dom</Link>)
            </>,
          ]}
        />

        <Entry
          date="July 5, 2021"
          additions={[
            <>
              You can now create your own widget groups! Simply drag a widget over another one and drop it to create a
              new group. Grouped widgets can still be filtered using the search field. To &quot;ungroup&quot; a widget
              simply drag it out of the group.
            </>,
          ]}
        />

        <Entry
          date="June 23, 2021"
          additions={[<>Show description as a tooltip for each widget on the dashboard.</>]}
        />

        <Entry
          date="June 16, 2021"
          additions={[
            <>Added suggestions based on user feedback. They can be added to the dashboard as a custom widget.</>,
          ]}
          improvements={[<>Amount of removed widgets is now showing up as a badge next to the &quot;+&quot; symbol</>]}
        />

        <Entry
          date="June 10, 2021"
          additions={[
            <>
              Added
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 inline-block -mt-1 ml-2 text-indigo-600 dark:text-indigo-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>{" "}
              dark mode
            </>,
          ]}
          improvements={[<>Fixed several smaller issues with the existing widgets</>]}
        />
        <Entry
          date="May 22, 2021"
          additions={[
            <>
              You can now add your own custom widgets!
              <div className="my-4">
                <ColorModeContent
                  dark={
                    <Image
                      src="/assets/screenshots/customWidgetDark.jpg"
                      alt="Add custom widgets"
                      width="736"
                      height="462"
                      className="rounded-lg shadow-md mt-1 mb-2"
                    />
                  }
                  light={
                    <Image
                      src="/assets/screenshots/customWidget.jpg"
                      alt="Add custom widgets"
                      width="736"
                      height="462"
                      className="rounded-lg shadow-md mt-1 mb-2"
                    />
                  }
                />
              </div>
            </>,

            <>Added possibility to download and import dashboard configuration</>,
          ]}
        />
        <Entry
          date="May 16, 2021"
          additions={[
            <>
              Added <Link to="/widget/css-triangle">CSS Triangle Generator</Link> widget
            </>,
          ]}
        />
        <Entry
          date="May 12, 2021"
          improvements={[
            <>
              Combined all SVG related widgets into <Link to="/widget/svg-converter">one</Link>
            </>,
            ,
            "Added icons to each widget to make them better identifiable",
          ]}
        />
        <Entry
          date="May 7, 2021"
          additions={[
            <>
              Added JavaScript output to the <Link to="/widget/css-converter">CSS converter</Link>
            </>,
          ]}
        />
        <Entry
          date="May 5, 2021"
          additions={[
            <>
              Added <Link to="/widget/regex-tester">Regex testing widget</Link>
            </>,
          ]}
        />
        <Entry
          date="May 2, 2021"
          improvements={["Widget modals have been made more consistent, so copying code should be easier"]}
        />
        <Entry
          date="May 1, 2021"
          additions={[
            <>
              Added generated TypeScript interfaces to <Link to="/widget/json-converter">JSON converter</Link>
            </>,
          ]}
        />
        <Entry
          date="April 29, 2021"
          improvements={[
            <>
              <Link to="/widget/css-svg-symbols">CSS & SVG Symbols widget</Link> now directly shows all icons when
              opened
            </>,
          ]}
        />
        <Entry
          date="April 20, 2021"
          additions={[
            <>
              Added <Link to="/widget/base64-converter">Base64 encoder/decoder</Link>
            </>,
          ]}
        />
        <Entry
          date="April 19, 2021"
          additions={["Added possibility to remove widgets", "Added separate (shareable) route for each widget"]}
        />
        <Entry
          date="April 18, 2021"
          additions={["Pasting CSS colors anywhere on the page automatically opens color converter"]}
          improvements={["Color converter modal is lazy loaded"]}
        />
        <Entry
          isLast
          date="April 13, 2021"
          additions={[
            "Pasting SVG code anywhere on the page automatically opens modal where one can choose a SVG related widget",
            <>
              Added JSX output to the <Link to="/widget/svg-converter">SVG converter</Link>
            </>,
          ]}
          improvements={["Widget modals are loaded dynamically"]}
        />
      </div>
    </Container>
  </div>
)

const Entry: FC<{
  date: string
  intro?: ReactNode
  isLast?: boolean
  additions?: ReactNode[]
  improvements?: ReactNode[]
}> = ({ intro, date, additions, improvements, isLast }) => (
  <>
    <div className="grid md:grid-cols-[1fr_3fr] gap-6">
      <div className="">
        <div className="font-bold text-gray-800 dark:text-gray-200">{date}</div>
      </div>
      <Card className="p-8 rounded-2xl">
        {intro && <p className="mb-6">{intro}</p>}
        {additions && (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-3 dark:text-gray-200">New additions</h3>

            <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
              {additions.map((addition, i) => (
                <li key={i}>{addition}</li>
              ))}
            </ul>
          </>
        )}
        {improvements && (
          <>
            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3 dark:text-gray-200">Improvements</h3>

            <ul className="list-disc list-outside ml-5 text-gray-500 dark:text-gray-400">
              {improvements.map((improvement, i) => (
                <li key={i}>{improvement}</li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
    {!isLast && (
      <div className="my-12 md:my-16">
        <Divider />
      </div>
    )}
  </>
)
